import { NavMarker } from 'react-rhumb';
import { Loadable } from 'foundations-components/transitional/utils/Loadable';
import { Layer } from 'foundations-components/transitional/components/Layer';
import I18n from 'I18n';
import FormattedHTMLMessage from 'I18n/components/FormattedHTMLMessage';
import PropTypes from 'prop-types';
import styled from 'foundations-theming/utils';
import { ErrorMessage } from 'foundations-components/transitional/components/ErrorMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { LoadingOverlay } from 'foundations-components/transitional/components/LoadingOverlay';
import { View } from 'foundations-components/components/View';
import { URANUS_LAYER } from 'foundations-components/transitional/constants';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export const delayImportUntilDocumentReady = loader => () => {
  return new Promise(resolve => {
    const interval = setInterval(() => {
      if (document.readyState === 'complete') {
        resolve(loader());
        clearInterval(interval);
      }
    }, 250);
  });
};
const AsyncComponentRetry = ({
  retry
}) => {
  return /*#__PURE__*/_jsx(ErrorMessage, {
    illustrationProps: {
      width: '100'
    },
    title: I18n.text('codeSplitting.retry.title'),
    children: /*#__PURE__*/_jsx(Button, {
      onClick: retry,
      size: "xs",
      use: "tertiary",
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "codeSplitting.retry.message"
      })
    })
  });
};
AsyncComponentRetry.propTypes = {
  retry: PropTypes.func.isRequired
};
export const AsyncComponentReload = () => {
  return /*#__PURE__*/_jsxs(View, {
    inlineSize: "100%",
    children: [/*#__PURE__*/_jsx(NavMarker, {
      name: "ASYNC_COMPONENT_FAILED_TO_LOAD"
    }), /*#__PURE__*/_jsx(ErrorMessage, {
      illustrationProps: {
        width: '100'
      },
      title: I18n.text('codeSplitting.reload.title'),
      children: /*#__PURE__*/_jsx(FormattedHTMLMessage, {
        message: "codeSplitting.reload.message"
      })
    })]
  });
};
const FullScreenWrapper = styled.div.withConfig({
  displayName: "makeAsyncUtils__FullScreenWrapper",
  componentId: "bhuktf-0"
})(["position:fixed;z-index:", ";top:0;left:0;right:0;bottom:0;"], URANUS_LAYER);
const makeAsyncComponentWithOverlay = (loader, {
  importName = null,
  delay = 500,
  waitUntilDocumentReadyToLoadDeps = false,
  maxRetries = 1
} = {}) => {
  let retries = 0;
  if (waitUntilDocumentReadyToLoadDeps) {
    loader = delayImportUntilDocumentReady(loader);
  }
  return Loadable({
    delay,
    loader: (...args) => {
      return loader(...args).then(imported => {
        if (importName) {
          return imported[importName];
        }
        return imported;
      });
    },
    LoadingComponent: ({
      pastDelay
    }) => {
      return pastDelay ? /*#__PURE__*/_jsx(Layer, {
        children: /*#__PURE__*/_jsx(FullScreenWrapper, {
          children: /*#__PURE__*/_jsx(LoadingOverlay, {
            "data-test-id": "loading-async-component"
          })
        })
      }) : null;
    },
    ErrorComponent: props => {
      let ErrorComponent = null;
      if (retries >= maxRetries) {
        ErrorComponent = /*#__PURE__*/_jsx(AsyncComponentReload, {});
      } else {
        ErrorComponent = /*#__PURE__*/_jsx(AsyncComponentRetry, {
          retry: props.retry
        });
      }
      retries++;
      return /*#__PURE__*/_jsx(Layer, {
        children: /*#__PURE__*/_jsx(FullScreenWrapper, {
          children: ErrorComponent
        })
      });
    }
  });
};
const makeAsyncComponent = (loader, {
  delay = 500,
  waitUntilDocumentReadyToLoadDeps = false,
  maxRetries = 1,
  containerStyles = {},
  throwErrorOnFail = false,
  extraProps = {},
  LoadingComponent = ({
    pastDelay
  }) => {
    return pastDelay ? /*#__PURE__*/_jsx("div", {
      style: Object.assign({
        height: '100%'
      }, containerStyles),
      children: /*#__PURE__*/_jsx(LoadingOverlay, {
        contextual: true
      })
    }) : null;
  }
} = {}) => {
  let retries = 0;
  if (waitUntilDocumentReadyToLoadDeps) {
    loader = delayImportUntilDocumentReady(loader);
  }
  return Loadable({
    delay,
    loader,
    LoadingComponent,
    ErrorComponent: props => {
      if (throwErrorOnFail) {
        throw props.error;
      } else {
        let ErrorComponent = null;
        if (retries >= maxRetries) {
          ErrorComponent = /*#__PURE__*/_jsx(AsyncComponentReload, {});
        } else {
          ErrorComponent = /*#__PURE__*/_jsx(AsyncComponentRetry, {
            retry: props.retry
          });
        }
        retries++;
        return /*#__PURE__*/_jsx("div", {
          style: {
            height: '100%'
          },
          children: ErrorComponent
        });
      }
    },
    render(module, props) {
      const Component = module.default || module;
      return /*#__PURE__*/_jsx(Component
      // Props from the parent component that is actually mounting this
      , Object.assign({}, props, extraProps));
    }
  });
};
const makeAsyncComponentWithNoStates = (loader, {
  waitUntilDocumentReadyToLoadDeps = false
} = {}) => {
  if (waitUntilDocumentReadyToLoadDeps) {
    loader = delayImportUntilDocumentReady(loader);
  }
  return Loadable({
    loader,
    LoadingComponent: () => null,
    ErrorComponent: props => {
      throw props.error;
    }
  });
};
export { makeAsyncComponentWithOverlay, makeAsyncComponent, makeAsyncComponentWithNoStates };